import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaBook, FaChartBar, FaCheckSquare, FaInbox, FaPlus, FaSlidersH } from "react-icons/fa";
import { TODOS_CONSTANTS } from "../_MODULES/_API";
import { LogbookTodoList } from "./LogbookTodoList";
import { Card, EmptyState, Skeleton, Badge } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { AdvancedFilterPanel } from "../../../../_UI/AdvancedFilterPanel";
import { StatisticsModal } from "../../../../_UI/StatisticsModal";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { ViewPagination } from "../../../../_UI/ViewPagination";
import { usePagedList } from "../../../../_UI/usePagedList";

const FILTERS = [
    { value: "all", label: "Todas" },
    { value: "pending", label: "Pendientes" },
    { value: "done", label: "Completadas" },
];

export const PanelTodos = ({ subProjectId, projectId }) => {

    const [useSearch, setSearch] = useState("");
    const [useFilter, setFilter] = useState("pending");
    const [useAdvancedOpen, setAdvancedOpen] = useState(false);
    const [statisticsOpen, setStatisticsOpen] = useState(false);
    const [useTodos, setTodos] = useState([]);
    const looseInputRef = useRef(null);

    const { apiGet: onGetTodos, useApiData: DATA, useApiLoading: IS_LOADING } = useApiQuery({
        minTimeQuery: 0, enablePagination: false, startLoading: true,
    });

    useEffect(() => {
        onGetTodos({ endpoint: TODOS_CONSTANTS.LIST, config: { params: { subProjectId } } });
    }, [subProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { setTodos(DATA ?? []); }, [DATA]);

    const onChanged = (event) => {
        if (event.action === "create") {
            setTodos((prev) => [...prev, event.todo]);
            return;
        }

        if (event.action === "confirm") {
            setTodos((prev) => prev.map((todo) => (todo.id === event.tempId ? event.todo : todo)));
            return;
        }

        if (event.action === "toggle") {
            setTodos((prev) => prev.map((todo) => (todo.id === event.id ? { ...todo, isDone: event.isDone } : todo)));
            return;
        }

        if (event.action === "favorite") {
            setTodos((prev) => prev.map((todo) => (todo.id === event.id ? { ...todo, isFavorite: event.isFavorite } : todo)));
            return;
        }

        if (event.action === "delete" || event.action === "archive") {
            setTodos((prev) => prev.filter((todo) => todo.id !== event.id));
        }
    };

    const SEARCHED_TODOS = useTodos.filter((todo) => onMatchesSearch(useSearch, [
        todo.title, todo.priority, todo.logbook?.title, todo.logbook?.type,
    ]));

    const COUNTS = {
        all: SEARCHED_TODOS.length,
        pending: SEARCHED_TODOS.filter((todo) => !todo.isDone).length,
        done: SEARCHED_TODOS.filter((todo) => todo.isDone).length,
    };

    const GROUPS = useMemo(() => {
        const LOOSE = [];
        const BY_ENTRY = new Map();

        SEARCHED_TODOS.forEach((todo) => {
            if (!todo.logbookId) {
                LOOSE.push(todo);
                return;
            }

            if (!BY_ENTRY.has(todo.logbookId)) {
                BY_ENTRY.set(todo.logbookId, {
                    id: todo.logbookId,
                    title: todo.logbook?.title ?? "Entrada de bitácora",
                    todos: [],
                });
            }

            BY_ENTRY.get(todo.logbookId).todos.push(todo);
        });

        return { loose: LOOSE, entries: [...BY_ENTRY.values()] };
    }, [SEARCHED_TODOS]);

    const onMatchFilter = (todo) => {
        if (useFilter === "pending") return !todo.isDone;
        if (useFilter === "done") return todo.isDone;
        return true;
    };

    const VISIBLE_ENTRIES = GROUPS.entries.filter((entry) => entry.todos.some(onMatchFilter));
    const PAGER = usePagedList(VISIBLE_ENTRIES, 6, "tasksPage");
    const VISIBLE_LOOSE_COUNT = GROUPS.loose.filter(onMatchFilter).length;
    const LINKED_COUNT = useTodos.filter((todo) => !!todo.logbookId).length;
    const LOOSE_COUNT = useTodos.length - LINKED_COUNT;

    const onFocusLooseTask = () => {
        setFilter("pending");
        requestAnimationFrame(() => looseInputRef.current?.focus?.());
    };

    return (
        <>
            <div className="LStudio__app_panel_bar">
                <ViewToolbar
                    filters={<>
                        <SearchFilter value={useSearch} onChange={setSearch}
                            total={useTodos.length} matches={SEARCHED_TODOS.length}
                            placeholder="Ej.: Revisar permisos, urgente o deploy..." />
                        <AppButton variant={useAdvancedOpen ? "soft" : "outline"} tone="neutral" icon={<FaSlidersH />}
                            onClick={() => setAdvancedOpen((current) => !current)}>
                            {useFilter === "pending" ? "Avanzado" : "Avanzado (1)"}
                        </AppButton>
                    </>}
                    expandedContent={<AdvancedFilterPanel isOpen={useAdvancedOpen}
                        activeCount={useFilter === "pending" ? 0 : 1} onClear={() => setFilter("pending")}>
                        <div className="LStudio__app_todos_filters">
                            {FILTERS.map((filter) => (
                                <button key={filter.value} type="button" className="LStudio__app_todos_filter"
                                    data-on={useFilter === filter.value} onClick={() => setFilter(filter.value)}>
                                    {filter.label}
                                    <span className="LStudio__app_todos_filter_count">{COUNTS[filter.value]}</span>
                                </button>
                            ))}
                        </div>
                    </AdvancedFilterPanel>}
                    total={SEARCHED_TODOS.length} perPage={PAGER.usePerPage} onChangePerPage={PAGER.onChangePerPage}
                    exportColumns={[
                        { key: "title", label: "Tarea" },
                        { key: "isDone", label: "Completada" },
                        { key: "priority", label: "Prioridad" },
                        { key: "logbook", label: "Entrada" },
                    ]}
                    exportRows={SEARCHED_TODOS.map((todo) => ({
                        title: todo.title,
                        isDone: todo.isDone ? "Sí" : "No",
                        priority: todo.priority ?? "",
                        logbook: todo.logbook?.title ?? "Suelta",
                    }))}
                    exportName="tareas"
                    actions={<>
                        <AppButton variant="outline" tone="neutral" icon={<FaChartBar />} isIconOnly
                            title="Estadísticas" aria-label="Estadísticas" onClick={() => setStatisticsOpen(true)} />
                        <AppButton icon={<FaPlus />} onClick={onFocusLooseTask}>Nueva tarea</AppButton>
                    </>} />
            </div>

            <p className="LStudio__app_todos_hint">
                <strong>Tareas</strong> sirve para pendientes concretos. Podés crear una tarea suelta directamente acá, sin usar Bitácora.
                La <strong>Bitácora</strong> es el historial de trabajo: documenta qué pasó, un problema, una decisión o una solución, y opcionalmente puede tener tareas vinculadas.
            </p>

            {IS_LOADING && <Skeleton variant="box" height={220} />}

            {!IS_LOADING && (
                <div className="LStudio__app_todos_groups">
                    <Card className="LStudio__app_todos_group LStudio__app_todos_group--loose">
                        <div className="LStudio__app_todos_group_head">
                            <FaInbox aria-hidden="true" />
                            <span className="LStudio__app_todos_group_title">Tareas sueltas</span>
                            <Badge tone="neutral" size="sm">
                                {GROUPS.loose.filter((todo) => todo.isDone).length} / {GROUPS.loose.length}
                            </Badge>
                        </div>
                        <p className="LStudio__app_todos_group_help">
                            Pendientes independientes del subproyecto. No necesitan una entrada de Bitácora.
                        </p>
                        <LogbookTodoList subProjectId={subProjectId} todos={GROUPS.loose}
                            filter={useFilter} isControlled onChanged={onChanged} inputRef={looseInputRef} />
                        {!!useSearch.trim() && VISIBLE_LOOSE_COUNT === 0 && GROUPS.loose.length === 0 && (
                            <small className="LStudio__app_todos_group_empty">No hay tareas sueltas que coincidan con la búsqueda.</small>
                        )}
                    </Card>

                    {PAGER.paged.map((entry) => (
                        <Card key={entry.id} className="LStudio__app_todos_group">
                            <div className="LStudio__app_todos_group_head">
                                <FaBook aria-hidden="true" />
                                <Link to={`/projects/${projectId}/subprojects/${subProjectId}/logbook`}
                                    className="LStudio__app_todos_group_title">
                                    {entry.title}
                                </Link>
                                <Badge tone="neutral" size="sm">
                                    {entry.todos.filter((todo) => todo.isDone).length} / {entry.todos.length}
                                </Badge>
                            </div>
                            <p className="LStudio__app_todos_group_help">Tareas vinculadas a esta entrada de Bitácora.</p>
                            <LogbookTodoList subProjectId={subProjectId} logbookId={entry.id}
                                todos={entry.todos} filter={useFilter} isControlled onChanged={onChanged} />
                        </Card>
                    ))}

                    {!!useSearch.trim() && SEARCHED_TODOS.length === 0 && useTodos.length > 0 && (
                        <EmptyState icon={<FaCheckSquare />} title="No encontramos tareas"
                            description="Probá con otra tarea, prioridad o nombre de entrada. Igual podés crear una tarea suelta arriba." />
                    )}
                </div>
            )}

            <ViewPagination page={PAGER.usePage} totalPages={PAGER.totalPages}
                total={PAGER.total} perPage={PAGER.usePerPage} onChange={PAGER.onChangePage} />

            <StatisticsModal isOpen={statisticsOpen} onClose={() => setStatisticsOpen(false)}
                title="Estadísticas · Tareas" description="Resumen de pendientes del subproyecto."
                metrics={[
                    { key: "all", label: "Tareas", value: useTodos.length, icon: <FaCheckSquare />, tone: "neutral", isLoading: IS_LOADING },
                    { key: "pending", label: "Pendientes", value: useTodos.filter((todo) => !todo.isDone).length, icon: <FaInbox />, tone: "accent", isLoading: IS_LOADING },
                    { key: "done", label: "Completadas", value: useTodos.filter((todo) => todo.isDone).length, icon: <FaCheckSquare />, tone: "ok", isLoading: IS_LOADING },
                    { key: "loose", label: "Sueltas", value: LOOSE_COUNT, icon: <FaInbox />, tone: "neutral", isLoading: IS_LOADING },
                    { key: "linked", label: "Vinculadas a Bitácora", value: LINKED_COUNT, icon: <FaBook />, tone: "neutral", isLoading: IS_LOADING },
                ]} />
        </>
    );
};
